import { createRouter, createWebHistory, RouteLocation } from 'vue-router'
import { sentryCapture } from '@/utils/sentry'
import { storeToRefs } from 'pinia'
import { ViewName, ScreenName, ScreenSetId, ProductId } from '@/types'
import { useAuthStore } from '@/store/AuthStore'
import CheckoutView from '@/views/CheckoutView.vue'
import AlreadyHaveView from '@/views/AlreadyHaveView.vue'
import PaymentMethod from '@/components/shared/screens/PaymentMethod.vue'
import Survey from '@/components/shared/screens/Survey.vue'
import Upgrade from '@/components/shared/screens/Upgrade.vue'
import Offer from '@/components/shared/screens/Offer.vue'
import CheckoutSuccess from '@/components/shared/screens/CheckoutSuccess.vue'
import TrialTimeline from '@/components/shared/screens/TrialTimeline.vue'

import {
  applyCouponToCheckout,
  checkAlreadyHas,
  checkCanNoPaymentRequiredCheckout,
  checkCanUpgrade,
  checkCouponCanBeAppliedToPlan,
  checkEligibleForActiveOffer,
  checkHasSelectedNfrPlan,
  checkHasSelectedPlan,
  checkHasUpgradePlan,
  checkIsEligibleForOffer,
  checkPlanProduct,
  checkRedirectToAlreadyHas,
  checkShouldRedirectToUpgrade,
  checkoutShouldShowTrialTimeline,
  checkoutStartEvent,
  fetchActiveOffer,
  fetchCoupon,
  fetchCoupons,
  fetchCustomerSurveyResponses,
  fetchDownloads,
  fetchFeatureFlags,
  fetchOfferContent,
  fetchPlans,
  fetchSubscriptions,
  fetchSubscriptionsAccount,
  fetchSurveys,
  fetchUpgradeContent,
  fetchUserGeoLocation,
  handleOrphanOfferPath,
  handleOrphanUpgradePath,
  handleQueryParameters,
  initializeCheckout,
  initializeScreens,
  initializeUser,
  loadRecurly,
  provideStoreState,
  redirectToLoginService,
  removeInvalidCoupon,
  removeReferrer,
  resetCheckout,
  resetUserAuth,
  setCheckoutSubscriptionId,
  setNoPaymentRequiredCheckout,
  setOffer,
  setOfferCoupon,
  setOfferPlan,
  setOfferProduct,
  setPlanFromCoupon,
  setProduct,
  setProductFromPlan,
  setScreen,
  setSelectedPlan,
  setSurveyId,
  setTrialTimelineScreen
} from '@/router/navigationGuards'
import UpgradeView from '@/views/UpgradeView.vue'
import OfferView from '@/views/OfferView.vue'
import { useProductStore } from '@/store/ProductStore'
import { useCouponStore } from '@/store/CouponStore'

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/subscriptions',
      name: 'subscriptions',
      redirect: to => {
        return {
          ...to,
          name: 'checkout',
          params: {
            productName: ProductId.Arcade
          }
        }
      }
    },
    {
      path: '/output-coproducer',
      alias: ['/coproducer', '/output-co-producer'],
      redirect: to => {
        return {
          ...to,
          path: '/co-producer'
        }
      }
    },
    {
      path: '/:productName?',
      name: 'checkout',
      component: CheckoutView,
      meta: {
        requiresAuth: true,
        defaultCreate: true,
        screenSet: ScreenSetId.Checkout
      },
      beforeEnter: [
        loadRecurly,
        initializeUser,
        fetchFeatureFlags,
        fetchPlans,
        fetchCoupons,
        fetchUserGeoLocation,
        setProduct,
        fetchSubscriptions,
        fetchSubscriptionsAccount,
        checkRedirectToAlreadyHas,
        initializeScreens,
        checkIsEligibleForOffer,
        checkShouldRedirectToUpgrade,
        fetchCustomerSurveyResponses,
        handleQueryParameters,
        fetchCoupon,
        removeReferrer,
        setSelectedPlan,
        checkHasSelectedPlan,
        checkCouponCanBeAppliedToPlan,
        removeInvalidCoupon,
        applyCouponToCheckout,
        checkPlanProduct,
        checkoutStartEvent,
        initializeCheckout,
        fetchDownloads,
        fetchSurveys,
        setSurveyId,
        setTrialTimelineScreen,
        setScreen
      ],
      children: [
        {
          path: '/:productName/survey/:surveyId',
          name: ScreenName.Survey,
          component: Survey,
          props: route => ({ screen: route.name, surveyId: route.params.surveyId })
        },
        {
          path: '/:productName/trial-timeline',
          name: ScreenName.TrialTimeline,
          component: TrialTimeline,
          beforeEnter: [checkoutShouldShowTrialTimeline],
          props: route => ({ screen: route.name })
        },
        {
          path: '/:productName/payment-method',
          name: ScreenName.PaymentMethod,
          component: PaymentMethod,
          props: route => ({ screen: route.name })
        },
        {
          path: '/:productName/confirmation',
          name: ScreenName.Confirmation,
          component: CheckoutSuccess,
          props: route => ({ screen: route.name })
        }
      ]
    },
    {
      path: '/existing/:productName',
      name: ScreenName.Existing,
      component: AlreadyHaveView,
      props: route => ({ screen: route.name }),
      meta: {
        requiresAuth: true,
        screenSet: ScreenSetId.Existing
      },
      beforeEnter: [checkAlreadyHas, initializeUser, setProduct, initializeScreens]
    },
    {
      path: '/success/:productName?',
      name: 'success',
      meta: {
        viewContextName: ViewName.Success
      },
      redirect: to => {
        const { query, params } = to
        const { path, ...toQuery } = query
        const { productName } = params
        const currentHost = `${window.location.protocol}//${window.location.host}`
        const returnPath = path ? decodeURI(path as string) : `/${productName}`
        const returnUrl = `${currentHost}${returnPath}`
        const isQuery = Object.entries(toQuery).length !== 0
        const queryString = Object.keys(toQuery)
          .map(key => `${key}=${toQuery[key]}`)
          .join('&')
        const redirectUrl = isQuery ? `${returnUrl}?${queryString}` : `${returnUrl}`

        window.location.replace(redirectUrl)
        return ''
      }
    },
    {
      path: '/:noPaymentPlanType(nfr|npi|internal)/:productName?',
      component: CheckoutView,
      name: 'no-payment-checkout',
      meta: {
        requiresAuth: true,
        defaultCreate: true,
        screenSet: ScreenSetId.NoPayPlan
      },
      beforeEnter: [
        initializeUser,
        fetchPlans,
        fetchUserGeoLocation,
        fetchSubscriptionsAccount,
        fetchSubscriptions,
        checkRedirectToAlreadyHas,
        handleQueryParameters,
        setNoPaymentRequiredCheckout,
        setProduct,
        fetchCoupon,
        removeInvalidCoupon,
        setPlanFromCoupon,
        checkHasSelectedNfrPlan,
        setProductFromPlan,
        checkCanNoPaymentRequiredCheckout,
        applyCouponToCheckout,
        checkoutStartEvent,
        initializeCheckout,
        initializeScreens,
        removeReferrer,
        fetchDownloads,
        setScreen
      ],
      children: [
        {
          path: '/:noPaymentPlanType(nfr|npi|internal)/:productName/payment-method',
          name: ScreenName.NoPayPlanPaymentMethod,
          component: PaymentMethod,
          props: route => ({ screen: route.name, planFeatureType: route.params.planFeatureType })
        },
        {
          path: '/:noPaymentPlanType(nfr|npi|internal)/:productName/confirmation',
          name: ScreenName.NoPayPlanConfirmation,
          component: CheckoutSuccess,
          beforeEnter: [fetchDownloads],
          props: route => ({ screen: route.name, planFeatureType: route.params.planFeatureType })
        }
      ]
    },
    {
      path: '/upgrade',
      component: UpgradeView,
      meta: {
        requiresAuth: true,
        screenSet: ScreenSetId.Upgrade
      },
      beforeEnter: [
        loadRecurly,
        handleOrphanUpgradePath,
        initializeUser,
        fetchUserGeoLocation,
        setProduct,
        fetchPlans,
        fetchCoupons,
        handleQueryParameters,
        initializeScreens,
        fetchSubscriptionsAccount,
        fetchSubscriptions,
        checkRedirectToAlreadyHas,
        checkCanUpgrade,
        removeReferrer,
        setSelectedPlan,
        checkHasUpgradePlan,
        checkHasSelectedPlan,
        checkIsEligibleForOffer,
        checkoutStartEvent,
        initializeCheckout,
        fetchCoupon,
        checkCouponCanBeAppliedToPlan,
        removeInvalidCoupon,
        applyCouponToCheckout,
        setCheckoutSubscriptionId,
        fetchUpgradeContent,
        fetchDownloads,
        setScreen
      ],
      children: [
        {
          path: '/upgrade/:productName',
          name: 'upgrade',
          component: Upgrade,

          props: route => ({ screen: route.name, full: true })
        },
        {
          path: '/upgrade/:productName/confirmation',
          name: ScreenName.UpgradeConfirmation,
          component: CheckoutSuccess,
          props: route => ({ screen: route.name })
        }
      ]
    },
    {
      path: '/offer',
      component: OfferView,
      meta: {
        requiresAuth: true,
        screenSet: ScreenSetId.Offer
      },
      beforeEnter: [
        loadRecurly,
        handleOrphanOfferPath,
        initializeUser,
        fetchPlans,
        fetchUserGeoLocation,
        fetchSubscriptions,
        fetchSubscriptionsAccount,

        removeReferrer,
        setOffer,
        fetchActiveOffer,
        checkRedirectToAlreadyHas,
        checkEligibleForActiveOffer,
        setOfferProduct,
        setOfferCoupon,
        initializeScreens,
        applyCouponToCheckout,
        setOfferPlan,
        setSelectedPlan,
        fetchOfferContent,
        checkHasSelectedPlan,
        checkoutStartEvent,
        initializeCheckout,
        fetchDownloads,
        setScreen
      ],
      children: [
        {
          path: '/offer/:offerId',
          name: 'offer',
          component: Offer
        },
        {
          path: '/offer/:offerId/confirmation',
          name: ScreenName.OfferConfirmation,
          component: CheckoutSuccess,
          props: route => ({ screen: route.name })
        }
      ]
    },
    {
      path: '/signin/:productName?',
      alias: '/login/:productName?',
      name: 'signIn',
      component: () => {
        return import('loginService/LoginView')
      },
      meta: {
        viewContextName: ViewName.Login
      },
      props: route => {
        const couponStore = useCouponStore()
        const { hasAppliedCoupon } = storeToRefs(couponStore)

        const badgeText = hasAppliedCoupon.value ? 'Coupon Applied' : ''
        return {
          query: route.query,
          params: route.params,
          isLogin: true,
          title: 'Log in to your account',
          description: 'Enter the email address associated with<br>your Output account.',
          pageTitle: 'Log in to your account',
          badgeText
        }
      },
      beforeEnter: [
        handleQueryParameters,
        fetchFeatureFlags,
        fetchCoupon,
        setProduct,
        removeInvalidCoupon,
        checkoutStartEvent,
        resetCheckout,
        provideStoreState
      ]
    },
    {
      path: '/create/:productName?',
      name: 'create',
      component: () => {
        return import('loginService/LoginView')
      },
      props: route => {
        const productStore = useProductStore()
        const productName = productStore.name || 'Arcade'
        const couponStore = useCouponStore()
        const { hasAppliedCoupon } = storeToRefs(couponStore)

        const badgeText = hasAppliedCoupon.value ? 'Coupon Applied' : ''

        return {
          query: route.query,
          params: route.params,
          isCreateNewAccount: true,
          title: `Sign up to create with ${productName}`,
          description: 'Enter your information then click<br>‘Create Account’ to get started.',
          pageTitle: `Sign up to create with ${productName}`,
          badgeText
        }
      },
      meta: {
        viewContextName: ViewName.Create
      },
      beforeEnter: [
        handleQueryParameters,
        fetchFeatureFlags,
        fetchCoupon,
        setProduct,
        removeInvalidCoupon,
        checkoutStartEvent,
        resetCheckout,
        provideStoreState
      ]
    },
    {
      path: '/logout/:productName?',
      name: 'logout',
      props: route => ({
        query: route.query,
        params: route.params,
        isLogout: true
      }),
      beforeEnter: [fetchFeatureFlags, resetUserAuth, setProduct, resetCheckout, provideStoreState],
      component: () => import('loginService/LoginView')
    }
  ]
})

// Update to Vue Router 4 syntax
router.beforeEach((to: RouteLocation) => {
  const { meta, query, name: toName, params, path } = to

  const authStore = useAuthStore()
  const { isUserAuthenticated } = storeToRefs(authStore)

  if (meta.requiresAuth && !isUserAuthenticated.value) {
    const referrer = toName as string
    const returnPath = path
    query.referrer = referrer
    query.path = returnPath
    // redirect the user to the login page
    const name = meta.defaultCreate ? 'create' : 'signIn'

    return {
      name,
      query: {
        ...query,
        referrer
      },
      params
    }
  }
})

router.onError((error, to) => {
  if (error.message.includes('Failed to fetch dynamically imported module')) {
    redirectToLoginService(to)
  } else {
    sentryCapture(error)
  }
})

export default router
